import { FeedPerformanceTemplateData, FeedPerformanceTemplate } from '../api/feed';

export interface FeedPerformanceData extends Omit<FeedPerformanceTemplateData, 'id' | 'templateId' | 'createdAt' | 'updatedAt'> {
  id?: string;
}

// Constants from Excel (D1 = 100000)
const STOCKING_DENSITY = 100000;

export class FeedPerformanceCalculator {
  /**
   * Calculate feed performance data based on Excel formulas
   */
  static calculateFeedPerformance(
    data: FeedPerformanceData,
    previousData?: FeedPerformanceData
  ): FeedPerformanceData {
    const result = { ...data };

    // Ensure numeric types for calculations to prevent string concatenation
    const abw = Number(result.abw) || 0;
    const feedPercentage = Number(result.feedPercentage) || 0;

    // Handle count field - it might contain invalid data like "5:" or "52"
    let count = 0;
    if (result.count) {
      const countStr = String(result.count).replace(/[^0-9]/g, ''); // Remove non-numeric characters
      count = Number(countStr) || 0;

      // Debug count parsing
      console.log(`Count parsing for DOC ${result.doc}:`, {
        rawCount: result.count,
        cleanedCountStr: countStr,
        parsedCount: count,
        countType: typeof count,
        isNaN: isNaN(count)
      });
    }

    const fcrPercentage = Number(result.fcrPercentage) || 0;

    // Debug logging
    console.log(`Calculating row DOC ${result.doc}:`, {
      abw,
      feedPercentage,
      count,
      fcrPercentage,
      previousCumulative: previousData?.cumulativeFeed
    });

    // Calculate Total Feed: =($D$1*B3*D3)/1000
    // Where D1 = STOCKING_DENSITY, B3 = ABW, D3 = FEED%
    if (abw > 0 && feedPercentage > 0) {
      result.totalFeed = (STOCKING_DENSITY * abw * feedPercentage) / 1000;
      // Round to 4 decimal places for consistency
      result.totalFeed = Math.round(result.totalFeed * 10000) / 10000;
    } else {
      result.totalFeed = 0;
    }

    // Calculate Feed distribution based on DOC
    if (result.totalFeed && result.totalFeed > 0) {
      this.calculateFeedDistribution(result);
    }

    // Calculate Cumulative Feed - ensure proper numeric calculation
    if (result.totalFeed && result.totalFeed > 0) {
      const previousCumulative = Number(previousData?.cumulativeFeed) || 0;
      const currentTotalFeed = Number(result.totalFeed) || 0;
      result.cumulativeFeed = previousCumulative + currentTotalFeed;
      // Round to 4 decimal places for consistency
      result.cumulativeFeed = Math.round(result.cumulativeFeed * 10000) / 10000;
    } else {
      result.cumulativeFeed = Number(previousData?.cumulativeFeed) || 0;
    }

    // Calculate Biomass: = CUMULATIVE FEED / FCR (correct aquaculture formula)
    if (result.cumulativeFeed && fcrPercentage > 0) {
      // FCR% is typically in decimal form (e.g., 1.15 = 1.15), not percentage
      const calculatedBiomass = Number(result.cumulativeFeed) / fcrPercentage;
      // Round to 2 decimal places for biomass display
      result.biomass = Math.round(calculatedBiomass * 100) / 100;

      // Debug biomass calculation
      console.log(`Biomass calculation for DOC ${result.doc}:`, {
        cumulativeFeed: result.cumulativeFeed,
        fcrPercentage,
        calculatedBiomass,
        roundedBiomass: result.biomass,
        formula: `${result.cumulativeFeed} / ${fcrPercentage} = ${calculatedBiomass} → rounded to ${result.biomass}`,
        originalBiomass: result.biomass
      });
    } else {
      result.biomass = 0;
      console.log(`Biomass calculation for DOC ${result.doc}: Invalid inputs - cumulativeFeed: ${result.cumulativeFeed}, fcrPercentage: ${fcrPercentage}`);
    }

    // Calculate Final Survival: = COUNT * BIOMASS (in kg)
    if (count > 0 && result.biomass && result.biomass > 0) {
      result.finalSurvival = count * result.biomass;
    } else {
      result.finalSurvival = 0;
    }

    // Ensure all numeric fields are properly set
    result.abw = abw;
    result.feedPercentage = feedPercentage;
    result.count = count;
    result.fcrPercentage = fcrPercentage;

    // Ensure biomass is properly set as a number
    if (typeof result.biomass !== 'number' || isNaN(result.biomass)) {
      console.warn(`Biomass type issue for DOC ${result.doc}:`, {
        originalType: typeof result.biomass,
        originalValue: result.biomass,
        isNaN: isNaN(result.biomass)
      });
      // Force it to be a number
      result.biomass = Number(result.biomass) || 0;
    }

    // Debug logging for results
    console.log(`Row DOC ${result.doc} calculated:`, {
      totalFeed: result.totalFeed,
      cumulativeFeed: result.cumulativeFeed,
      biomass: result.biomass,
      finalSurvival: result.finalSurvival,
      count: result.count,
      abw: result.abw
    });

    return result;
  }

  /**
   * Calculate feed distribution across feed1, feed2, feed3, feed4 based on DOC
   */
  private static calculateFeedDistribution(data: FeedPerformanceData): void {
    if (!data.totalFeed || data.totalFeed <= 0) return;

    const doc = Number(data.doc) || 0;
    let divisor = 1;

    // Determine divisor based on DOC (from Excel formulas)
    if (doc <= 4) {
      divisor = 2; // FEED4 = TOT_FEED/2
    } else if (doc <= 6) {
      divisor = 3; // FEED1, FEED3, FEED4 = TOT_FEED/3
    } else {
      divisor = 4; // FEED1, FEED2, FEED3, FEED4 = TOT_FEED/4
    }

    const feedPerType = Number(data.totalFeed) / divisor;

    // Set feed values based on DOC - ensure numeric values
    if (doc <= 4) {
      data.feed4 = feedPerType;
      data.feed1 = 0;
      data.feed2 = 0;
      data.feed3 = 0;
    } else if (doc <= 6) {
      data.feed1 = feedPerType;
      data.feed2 = 0;
      data.feed3 = feedPerType;
      data.feed4 = feedPerType;
    } else {
      data.feed1 = feedPerType;
      data.feed2 = feedPerType;
      data.feed3 = feedPerType;
      data.feed4 = feedPerType;
    }
  }

  /**
   * Recalculate all data in a template
   */
  static recalculateTemplate(template: FeedPerformanceTemplate): FeedPerformanceTemplate {
    if (!template.templateData) return template;

    const recalculatedData: FeedPerformanceTemplateData[] = [];

    for (let i = 0; i < template.templateData.length; i++) {
      const currentData = template.templateData[i];
      const previousData = i > 0 ? recalculatedData[i - 1] : undefined;

      const calculatedData = this.calculateFeedPerformance(currentData, previousData);
      recalculatedData.push({
        ...calculatedData,
        id: currentData.id,
        templateId: currentData.templateId,
        createdAt: currentData.createdAt,
        updatedAt: currentData.updatedAt
      });
    }

    return {
      ...template,
      templateData: recalculatedData
    };
  }

  /**
   * Update a single row and recalculate subsequent rows
   */
  static updateRowAndRecalculate(
    template: FeedPerformanceTemplate,
    rowIndex: number,
    updatedData: Partial<FeedPerformanceData>
  ): FeedPerformanceTemplate {
    if (!template.templateData) return template;

    const newTemplateData = [...template.templateData];

    // Update the specific row with the new data
    newTemplateData[rowIndex] = {
      ...newTemplateData[rowIndex],
      ...updatedData
    };

    // Recalculate from this row onwards to ensure all dependent calculations are correct
    for (let i = rowIndex; i < newTemplateData.length; i++) {
      const currentData = { ...newTemplateData[i] };
      const previousData = i > 0 ? newTemplateData[i - 1] : undefined;

      // Recalculate all fields for this row
      const calculatedData = this.calculateFeedPerformance(currentData, previousData);

      // Preserve the original ID and metadata
      newTemplateData[i] = {
        ...calculatedData,
        id: currentData.id,
        templateId: currentData.templateId,
        createdAt: currentData.createdAt,
        updatedAt: currentData.updatedAt
      };
    }

    return {
      ...template,
      templateData: newTemplateData
    };
  }
} 